import { create, el } from "@dhx/ts-common/dom";
import { locateNode } from "@dhx/ts-common/html";
import { IEventSystem } from "@dhx/ts-common/events";
import { DataEvents } from "@dhx/ts-data";
import { ISidebarConfig, SidebarEvents, ISidebarElement, ISidebar, ISidebarEventHandlersMap } from "./types";
import { Position, tooltip } from "@dhx/ts-message";
import { createFactory, Navbar, NavigationBarEvents } from "@dhx/ts-navbar";
import { extend } from "@dhx/ts-common/core";
import { Id } from "@dhx/ts-common/types";

export class Sidebar extends Navbar<ISidebarElement> implements ISidebar {
	public events: IEventSystem<
		NavigationBarEvents | DataEvents | (SidebarEvents & ISidebarEventHandlersMap)
	>;
	public config: ISidebarConfig;
	private _waitRestore: boolean;

	constructor(element?: string | HTMLElement, config?) {
		super(
			element,
			extend(
				{
					navigationType: "click",
					width: "200",
					minWidth: "44",
					collapsed: false,
				},
				config
			)
		);

		this._currentRoot = null;
		const render = () => this._draw();
		this.mount(element, create({ render }));
	}
	toggle(): void {
		if (this.config.collapsed) {
			this.expand();
		} else {
			this.collapse();
		}
		this.events.fire(SidebarEvents.toggle, [this.config.collapsed]); // TODO: remove suite_7.0
		this.paint();
	}
	collapse(): void {
		if (!this.events.fire(SidebarEvents.beforeCollapse, [])) {
			return;
		}
		this.config.collapsed = true;
		this.events.fire(SidebarEvents.afterCollapse, []);
		this.paint();
	}
	expand(): void {
		if (!this.events.fire(SidebarEvents.beforeExpand, [])) {
			return;
		}
		this.config.collapsed = false;
		this.events.fire(SidebarEvents.afterExpand, []);
		this.paint();
	}
	isCollapsed(): boolean {
		return this.config.collapsed;
	}
	protected _getFactory() {
		return createFactory({
			widget: this,
			defaultType: "navItem",
			allowedTypes: [
				"navItem",
				"menuItem",
				"customHTML",
				"separator",
				"spacer",
				"title",

				"customHTMLButton",
			],
			widgetName: "sidebar",
		});
	}
	protected _close(e: MouseEvent): void {
		this._activePosition = null;
		this._currentRoot = null;
		super._close(e);
	}
	protected _setRoot(id: Id) {
		if (this.data.getParent(id) === this.data.getRoot()) {
			this._currentRoot = id;
		}
	}
	protected _customHandlers() {
		return {
			tooltip: (e: MouseEvent) => {
				const elem = locateNode(e);
				if (!elem) {
					return;
				}
				const id = elem.getAttribute("data-dhx-id");
				const item = this.data.getItem(id);
				if (item.tooltip || (this.config.collapsed && item.value)) {
					tooltip(item.tooltip || item.value, {
						node: elem as HTMLElement,
						position: Position.right,
					});
				}
			},
		};
	}
	protected _draw() {
		const { width, minWidth } = this.config;
		const actualWidth = this.config.collapsed ? minWidth : width;
		return el(
			"nav.dhx_widget.dhx_sidebar",
			{
				class:
					(this.config.css ? this.config.css : "") +
					(this.config.collapsed ? " dhx_sidebar--minimized" : ""),
				style: { width: actualWidth + "px" },
			},
			[
				el(
					"ul.dhx_navbar.dhx_navbar--vertical",
					{
						"data-dhx-widget-id": this._uid,
						tabindex: 0,
						onclick: this._handlers.onclick,
						onmousedown: this._handlers.onmousedown,
						oninput: this._handlers.input,
						onmouseover: this._handlers.tooltip,
						_hooks: {
							didInsert: node => {
								node.el.addEventListener(
									"keyup",
									e => {
										if (e.which !== 9) {
											return;
										}
										const elem = locateNode(document.activeElement);
										if (!elem) {
											return;
										}

										const id = elem.getAttribute("data-dhx-id");
										const item = this.data.getItem(id);
										if (item.tooltip || (this.config.collapsed && item.value)) {
											tooltip(item.tooltip || item.value, {
												node: elem as HTMLElement,
												position: Position.right,
												force: true,
											});
										}
									},
									true
								);
							},
						},
					},
					this.data.map(item => this._factory(item), this.data.getRoot(), false)
				),
			]
		);
	}
	protected _getMode(): "right" | "bottom" {
		return "right";
	}
	protected _customInitEvents() {
		this.events.on(NavigationBarEvents.inputBlur, () => {
			if (this._waitRestore) {
				this.toggle();
				this._waitRestore = false;
			}
		});
		this.events.on(NavigationBarEvents.inputFocus, () => {
			if (this.config.collapsed) {
				this._waitRestore = true;
				this.toggle();
			}
		});
	}
}
